<template>
  <el-dialog
      title="配置数据源"
      :visible.sync="dialogVisible"
      width="520px"
      :close-on-click-modal="false"
      append-to-body
      @closed="handleClosed"
  >
    <el-form :model="form" :rules="rules" ref="sourceConfigForm" label-width="100px" size="small">
      <el-form-item label="所属模块">
        <el-input :value="form.moduleName" disabled></el-input>
      </el-form-item>

      <el-form-item label="数据源" prop="dataSourceId">
        <div style="display:flex;gap:8px;width:100%;">
          <el-select v-model="form.dataSourceId" placeholder="请选择数据源" style="flex:1;" filterable
                     @change="onSourceChange">
            <el-option
                v-for="item in sourceOptions"
                :key="item.id"
                :label="item.name"
                :value="item.id">
            </el-option>
          </el-select>
          <el-button type="primary" plain size="small" @click="$emit('go-source')">
            <i class="el-icon-link"></i> 管理
          </el-button>
        </div>
      </el-form-item>

      <el-form-item label="数据库" prop="name">
        <el-select
            v-model="form.name"
            placeholder="请选择或输入数据库名"
            style="width:100%;"
            filterable
            allow-create
            default-first-option
            :loading="dbLoading"
            @change="onDatabaseChange"
        >
          <el-option
              v-for="item in databaseOptions"
              :key="item.id"
              :label="item.name"
              :value="item.name">
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="作者名" prop="authorName">
        <el-input v-model="form.authorName" placeholder="请输入作者名"></el-input>
      </el-form-item>

      <el-form-item label="包名" prop="packageName">
        <el-input v-model="form.packageName" placeholder="com.yida.module"></el-input>
      </el-form-item>

      <el-form-item prop="prefix">
        <label-tip label="表前缀" tip="表前缀：（如：sys_example,则此处填写'sys'即可），目的是为了代码生成的时候去掉前缀" slot="label"/>
        <el-input v-model="form.prefix" placeholder="例如：t_（为空则不处理）"></el-input>
      </el-form-item>

      <el-form-item label="模块描述" prop="moduleDescription">
        <el-input v-model="form.moduleDescription" type="textarea" :rows="2" placeholder="请输入模块描述"></el-input>
      </el-form-item>

      <el-form-item label="备注" prop="comment">
        <el-input v-model="form.comment" placeholder="请输入备注"></el-input>
      </el-form-item>

      <el-collapse style="border:none;margin-top:-8px;">
        <el-collapse-item title="高级路径配置">
          <el-form-item prop="javaPath">
            <label-tip label="后端路径" tip="后端路径填写项目根目录下的相对路径即可，如：/process-domain/data/src/main/java/com/yida" slot="label"/>
            <directory-select v-model="form.javaPath" placeholder="例如：/process-domain/data/src/main/java/com/yida"></directory-select>
          </el-form-item>
          <el-form-item prop="vuePath">
            <label-tip label="前端路径" tip="前端路径填入项目根目录的相对路径即可，如：/redid-vue/src/views/data" slot="label"/>
            <directory-select v-model="form.vuePath" placeholder="例如：/redid-vue/src/views/data"></directory-select>
          </el-form-item>
        </el-collapse-item>
      </el-collapse>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="$emit('update:visible', false)" size="small">取 消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting" size="small">
        <i class="el-icon-check"></i> 确 定
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
/**
 * 模块数据源配置表单
 * @author PeterLee
 * @since 2025-05-11
 */
import { getList } from "@/api/baseApi";
import LabelTip from "@/components/widget/LabelTip";
import DirectorySelect from "@/components/select/DirectorySelect";

export default {
  name: 'SourceConfigForm',
  components: {
    LabelTip,
    DirectorySelect
  },
  props: {
    visible: Boolean,
    sourceOptions: { type: Array, default: () => [] },
    initialForm: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      submitting: false,
      dbLoading: false,
      databaseOptions: [],
      form: {
        dataSourceId: null,
        name: '',
        dataBaseId: null,
        moduleName: '',
        authorName: '',
        packageName: '',
        prefix: '',
        moduleDescription: '',
        comment: '',
        javaPath: '',
        vuePath: ''
      },
      rules: {
        dataSourceId: [{ required: true, message: '请选择数据源', trigger: 'change' }],
        name: [{ required: true, message: '请选择或输入数据库名', trigger: ['change', 'blur'] }],
        authorName: [{ required: true, message: '请输入作者名', trigger: 'blur' }],
        packageName: [{ required: true, message: '请输入包名', trigger: 'blur' }],
        moduleDescription: [{ required: true, message: '请输入模块描述', trigger: 'blur' }],
        comment: [{ required: true, message: '请输入备注', trigger: 'blur' }]
      }
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    }
  },
  watch: {
    initialForm: {
      immediate: true,
      async handler(val) {
        this.form = {
          dataSourceId: val.dataSourceId || null,
          name: val.name || '',
          dataBaseId: val.dataBaseId || null,
          moduleName: val.moduleName || '',
          authorName: val.authorName || 'Auto Generated',
          packageName: val.packageName || '',
          prefix: val.prefix || '',
          moduleDescription: val.moduleDescription || '',
          comment: val.comment || '',
          javaPath: val.javaPath || '',
          vuePath: val.vuePath || ''
        };
        this.databaseOptions = [];
        // 已有数据源时自动加载数据库选项，否则下拉列表为空
        if (val.dataSourceId) {
          await this.loadDatabases(val.dataSourceId);
        }
        this.$nextTick(() => {
          if (this.$refs.sourceConfigForm) {
            this.$refs.sourceConfigForm.clearValidate();
          }
        });
      }
    }
  },
  methods: {
    async onSourceChange(sourceId) {
      if (!sourceId) {
        this.databaseOptions = [];
        return;
      }
      await this.loadDatabases(sourceId);
    },
    async loadDatabases(sourceId) {
      this.dbLoading = true;
      try {
        const res = await getList('data', 'dataBase');
        const allDatabases = res.data || [];
        this.databaseOptions = allDatabases.filter(
          db => db.dataSourceId === sourceId
        );
      } catch (error) {
        console.error('加载数据库列表失败:', error);
        this.databaseOptions = [];
      } finally {
        this.dbLoading = false;
      }
    },
    onDatabaseChange(dbName) {
      if (!dbName) return;
      const matched = this.databaseOptions.find(db => db.name === dbName);
      if (matched) {
        this.form.dataBaseId = matched.id;
        this.form.authorName = matched.authorName || this.form.authorName;
        this.form.packageName = matched.packageName || this.form.packageName;
        this.form.prefix = matched.prefix || '';
        this.form.moduleDescription = matched.moduleDescription || this.form.moduleDescription;
        this.form.comment = matched.comment || this.form.comment;
        this.form.javaPath = matched.javaPath || '';
        this.form.vuePath = matched.vuePath || '';
      }
    },
    async handleSubmit() {
      try {
        await this.$refs.sourceConfigForm.validate();
      } catch {
        return;
      }
      this.submitting = true;
      this.$emit('submit', { ...this.form }, () => {
        this.submitting = false;
      });
    },
    handleClosed() {
      this.submitting = false;
      this.databaseOptions = [];
    }
  }
}
</script>
